const DEPLOYMENT_UPDATE_SIGNAL_LISTENERS = new Set();

export const DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY = "grain-deployment-auto-reload-version";
export const DEFAULT_DEPLOYMENT_UPDATE_TIMINGS = Object.freeze({
  monitorIntervalMs: 5000,
  readinessIntervalMs: 2000,
  readinessTimeoutMs: 60000,
  requestTimeoutMs: 5000,
  reloadDelayMs: 5000,
});

const TRANSIENT_DEPLOYMENT_STATUS_CODES = new Set([500, 502, 503, 504]);

export function normalizeDeploymentAssetVersion(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function shouldRequestDeploymentVersionCheck(status) {
  const normalizedStatus = Number(status);
  return Number.isInteger(normalizedStatus) && TRANSIENT_DEPLOYMENT_STATUS_CODES.has(normalizedStatus);
}

export function readLoadedDeploymentAssetVersion({
  documentRef = typeof document === "undefined" ? null : document,
  baseUrl = typeof window === "undefined" ? "http://127.0.0.1/" : window.location.href,
} = {}) {
  if (!documentRef || typeof documentRef.querySelectorAll !== "function") {
    return "";
  }

  // En local, Vite charge main.jsx sans version ; la feuille du document la porte.
  for (const [selector, attribute, assetPath] of [
    ["script[src]", "src", "/static/react/reassignment.js"],
    ['link[rel="stylesheet"][href]', "href", "/static/react/reassignment.css"],
  ]) {
    for (const element of documentRef.querySelectorAll(selector)) {
      const rawSource = element?.[attribute] || element?.getAttribute?.(attribute);
      if (typeof rawSource !== "string" || !rawSource.trim()) continue;

      try {
        const assetUrl = new URL(rawSource, baseUrl);
        if (!assetUrl.pathname.endsWith(assetPath)) continue;
        const version = normalizeDeploymentAssetVersion(assetUrl.searchParams.get("v"));
        if (version) return version;
      } catch {
        // Ignore les références non résolubles et poursuit la recherche.
      }
    }
  }

  return "";
}

export function subscribeToDeploymentUpdateSignals(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  DEPLOYMENT_UPDATE_SIGNAL_LISTENERS.add(listener);
  return () => {
    DEPLOYMENT_UPDATE_SIGNAL_LISTENERS.delete(listener);
  };
}

function publishDeploymentUpdateSignal(signal) {
  DEPLOYMENT_UPDATE_SIGNAL_LISTENERS.forEach((listener) => {
    try {
      listener(signal);
    } catch {
      // Un observateur défaillant ne doit jamais interrompre une requête métier.
    }
  });
}

export function reportObservedDeploymentVersion(value, source = "unknown") {
  const version = normalizeDeploymentAssetVersion(value);
  if (!version) {
    return false;
  }

  publishDeploymentUpdateSignal({ type: "version", version, source });
  return true;
}

export function requestDeploymentVersionCheck(reason = "manual") {
  publishDeploymentUpdateSignal({ type: "check", reason });
}

function safeReadStorage(storage, key) {
  try {
    return normalizeDeploymentAssetVersion(storage?.getItem?.(key));
  } catch {
    return "";
  }
}

function safeWriteStorage(storage, key, value) {
  try {
    storage?.setItem?.(key, value);
  } catch {
    // Le rechargement reste possible lorsque sessionStorage est indisponible.
  }
}

function safeRemoveStorage(storage, key) {
  try {
    storage?.removeItem?.(key);
  } catch {
    // Aucun impact fonctionnel si le navigateur refuse l'accès au stockage.
  }
}

function createInitialState(reloadDelayMs) {
  return {
    phase: "monitoring",
    targetVersion: "",
    countdownSeconds: Math.max(1, Math.ceil(reloadDelayMs / 1000)),
  };
}

export function createDeploymentUpdateController({
  loadedVersion: initialLoadedVersion = "",
  fetchImpl = typeof globalThis.fetch === "function" ? globalThis.fetch.bind(globalThis) : null,
  reload = () => {},
  storage = null,
  now = () => Date.now(),
  setTimeoutImpl = globalThis.setTimeout?.bind(globalThis),
  clearTimeoutImpl = globalThis.clearTimeout?.bind(globalThis),
  timings: timingOverrides = {},
} = {}) {
  const timings = {
    ...DEFAULT_DEPLOYMENT_UPDATE_TIMINGS,
    ...(timingOverrides || {}),
  };
  let loadedVersion = normalizeDeploymentAssetVersion(initialLoadedVersion);
  let state = createInitialState(timings.reloadDelayMs);
  let isDisposed = false;
  let isStarted = false;
  let isVisible = true;
  let monitorTimerId = null;
  let readinessTimerId = null;
  let readinessDeadlineTimerId = null;
  let countdownTimerId = null;
  let versionCheckPromise = null;
  let readinessCheckPromise = null;
  let readinessStartedAt = null;
  let countdownDeadline = null;
  const listeners = new Set();

  if (loadedVersion && safeReadStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY) === loadedVersion) {
    safeRemoveStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY);
  }

  const emitState = (updates) => {
    state = { ...state, ...(updates || {}) };
    listeners.forEach((listener) => {
      try {
        listener(state);
      } catch {
        // L'état du moniteur ne dépend pas du rendu d'un observateur.
      }
    });
  };

  const cancelTimer = (timerId) => {
    if (timerId != null && typeof clearTimeoutImpl === "function") {
      clearTimeoutImpl(timerId);
    }
  };

  const clearMonitorTimer = () => {
    cancelTimer(monitorTimerId);
    monitorTimerId = null;
  };

  const clearReadinessTimer = () => {
    cancelTimer(readinessTimerId);
    readinessTimerId = null;
  };

  const clearReadinessDeadlineTimer = () => {
    cancelTimer(readinessDeadlineTimerId);
    readinessDeadlineTimerId = null;
  };

  const clearCountdownTimer = () => {
    cancelTimer(countdownTimerId);
    countdownTimerId = null;
  };

  const schedule = (callback, delayMs) => {
    if (typeof setTimeoutImpl !== "function") {
      return null;
    }
    return setTimeoutImpl(callback, Math.max(0, Number(delayMs) || 0));
  };

  const fetchHealth = async (url) => {
    if (typeof fetchImpl !== "function") {
      return null;
    }

    const abortController = typeof globalThis.AbortController === "function"
      ? new globalThis.AbortController()
      : null;
    const requestInit = {
      cache: "no-store",
      ...(abortController ? { signal: abortController.signal } : {}),
    };
    const requestPromise = Promise.resolve()
      .then(() => fetchImpl(url, requestInit))
      .catch(() => null);

    if (!(timings.requestTimeoutMs > 0) || typeof setTimeoutImpl !== "function") {
      return requestPromise;
    }

    let requestTimerId = null;
    const timeoutPromise = new Promise((resolve) => {
      requestTimerId = schedule(() => {
        abortController?.abort();
        resolve(null);
      }, timings.requestTimeoutMs);
    });

    try {
      return await Promise.race([requestPromise, timeoutPromise]);
    } finally {
      cancelTimer(requestTimerId);
    }
  };

  const reloadNow = () => {
    if (isDisposed || state.phase === "reloading" || !state.targetVersion) {
      return false;
    }

    clearMonitorTimer();
    clearReadinessTimer();
    clearReadinessDeadlineTimer();
    clearCountdownTimer();
    safeWriteStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY, state.targetVersion);
    emitState({ phase: "reloading" });
    reload();
    return true;
  };

  const scheduleCountdownTick = () => {
    clearCountdownTimer();
    if (isDisposed || state.phase !== "countdown" || countdownDeadline == null) {
      return;
    }

    const remainingMs = Math.max(0, countdownDeadline - now());
    if (remainingMs <= 0) {
      reloadNow();
      return;
    }

    const countdownSeconds = Math.max(1, Math.ceil(remainingMs / 1000));
    if (countdownSeconds !== state.countdownSeconds) {
      emitState({ countdownSeconds });
    }
    countdownTimerId = schedule(scheduleCountdownTick, Math.min(1000, remainingMs));
  };

  const beginCountdown = () => {
    clearReadinessTimer();
    clearReadinessDeadlineTimer();
    readinessStartedAt = null;
    countdownDeadline = now() + timings.reloadDelayMs;
    emitState({
      phase: "countdown",
      countdownSeconds: Math.max(1, Math.ceil(timings.reloadDelayMs / 1000)),
    });
    scheduleCountdownTick();
  };

  const scheduleReadinessCheck = (checkReadiness) => {
    clearReadinessTimer();
    if (isDisposed || state.phase !== "waiting_ready") {
      return;
    }
    readinessTimerId = schedule(() => {
      void checkReadiness();
    }, timings.readinessIntervalMs);
  };

  const scheduleReadinessDeadline = () => {
    clearReadinessDeadlineTimer();
    readinessDeadlineTimerId = schedule(() => {
      readinessDeadlineTimerId = null;
      if (isDisposed || state.phase !== "waiting_ready") {
        return;
      }
      clearReadinessTimer();
      emitState({ phase: "delayed" });
    }, timings.readinessTimeoutMs);
  };

  const checkReadiness = async () => {
    if (isDisposed || state.phase !== "waiting_ready") {
      return false;
    }
    if (readinessCheckPromise) {
      return readinessCheckPromise;
    }

    readinessCheckPromise = (async () => {
      let isReady = false;
      const response = await fetchHealth("/health/ready");
      isReady = Boolean(response?.ok);

      if (isDisposed || state.phase !== "waiting_ready") {
        return false;
      }
      if (isReady) {
        beginCountdown();
        return true;
      }

      const elapsedMs = Math.max(0, now() - (readinessStartedAt ?? now()));
      if (elapsedMs >= timings.readinessTimeoutMs) {
        clearReadinessTimer();
        clearReadinessDeadlineTimer();
        emitState({ phase: "delayed" });
        return false;
      }

      scheduleReadinessCheck(checkReadiness);
      return false;
    })().finally(() => {
      readinessCheckPromise = null;
    });

    return readinessCheckPromise;
  };

  const beginUpdate = (targetVersion) => {
    const normalizedTargetVersion = normalizeDeploymentAssetVersion(targetVersion);
    if (!normalizedTargetVersion || normalizedTargetVersion === loadedVersion || state.phase === "reloading") {
      return false;
    }
    if (state.targetVersion === normalizedTargetVersion && state.phase !== "monitoring") {
      return true;
    }

    clearMonitorTimer();
    clearReadinessTimer();
    clearReadinessDeadlineTimer();
    clearCountdownTimer();
    countdownDeadline = null;
    readinessStartedAt = null;

    if (safeReadStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY) === normalizedTargetVersion) {
      emitState({
        phase: "manual",
        targetVersion: normalizedTargetVersion,
      });
      return true;
    }

    readinessStartedAt = now();
    scheduleReadinessDeadline();
    emitState({
      phase: "waiting_ready",
      targetVersion: normalizedTargetVersion,
    });
    void checkReadiness();
    return true;
  };

  const reportServerVersion = (value) => {
    const serverVersion = normalizeDeploymentAssetVersion(value);
    if (!serverVersion || isDisposed) {
      return false;
    }

    if (!loadedVersion) {
      loadedVersion = serverVersion;
      if (safeReadStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY) === serverVersion) {
        safeRemoveStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY);
      }
      return false;
    }

    if (serverVersion === loadedVersion) {
      if (safeReadStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY) === serverVersion) {
        safeRemoveStorage(storage, DEPLOYMENT_UPDATE_RELOAD_STORAGE_KEY);
      }
      return false;
    }

    return beginUpdate(serverVersion);
  };

  const scheduleNextMonitorCheck = (requestVersionCheck) => {
    clearMonitorTimer();
    if (
      isDisposed
      || !isStarted
      || !isVisible
      || state.phase !== "monitoring"
    ) {
      return;
    }
    monitorTimerId = schedule(() => {
      void requestVersionCheck("interval");
    }, timings.monitorIntervalMs);
  };

  const requestVersionCheck = async () => {
    if (isDisposed || state.phase === "reloading") {
      return false;
    }
    if (versionCheckPromise) {
      return versionCheckPromise;
    }

    clearMonitorTimer();
    versionCheckPromise = (async () => {
      try {
        const response = await fetchHealth("/health/live");
        if (!response?.ok) {
          return false;
        }
        const payload = await response.json();
        return reportServerVersion(payload?.asset_version);
      } catch {
        return false;
      }
    })().finally(() => {
      versionCheckPromise = null;
      scheduleNextMonitorCheck(requestVersionCheck);
    });

    return versionCheckPromise;
  };

  const retryReadiness = () => {
    if (isDisposed || !state.targetVersion) {
      return false;
    }
    clearReadinessTimer();
    clearReadinessDeadlineTimer();
    readinessStartedAt = now();
    emitState({ phase: "waiting_ready" });
    scheduleReadinessDeadline();
    void checkReadiness();
    return true;
  };

  return {
    dispose() {
      isDisposed = true;
      clearMonitorTimer();
      clearReadinessTimer();
      clearReadinessDeadlineTimer();
      clearCountdownTimer();
      listeners.clear();
    },
    getState() {
      return state;
    },
    reloadNow,
    reportServerVersion,
    requestVersionCheck,
    retryReadiness,
    setVisible(nextVisible) {
      const resolvedVisible = nextVisible !== false;
      if (isVisible === resolvedVisible) {
        return;
      }
      isVisible = resolvedVisible;
      if (!isVisible) {
        clearMonitorTimer();
        return;
      }
      if (isStarted && state.phase === "monitoring") {
        void requestVersionCheck("visible");
      }
    },
    start() {
      if (isDisposed || isStarted) {
        return;
      }
      isStarted = true;
      if (isVisible) {
        void requestVersionCheck("startup");
      }
    },
    subscribe(listener) {
      if (typeof listener !== "function") {
        return () => {};
      }
      listeners.add(listener);
      listener(state);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
